import React, {FC, useEffect} from 'react';
import styled from 'styled-components';

import {defineMessage, Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
  WorkspaceDisplayItemsEnum,
} from '../../../../services/tracking';
import {InboxesPanelId} from '../../../../store/panels/panelStateBase';
import {LeftPanel} from '../../../app/leftPanel';
import {useWorkspacesEditor} from './context/workspacesEditorContext';
import {LensesWorkspaceEditorContainer} from './lensesWorkspaceEditorContainer';
import {TeammatesWorkspaceEditorContainer} from './teammatesWorkspaceEditorContainer';
import {TeammateWorkspaceEditorContainer} from './teammateWorkspaceEditorContainer';
import {TeamWorkspaceEditorContainer} from './teamWorkspaceEditorContainer';
import {WorkspacesEditorAutoRecHelpTipContainer} from './workspacesEditorAutoRecHelpTipContainer';
import {WorkspacesEditorFooter} from './workspacesEditorFooter';

/*
 * Props.
 */

export interface WorkspacesEditorProps {
  panelId: InboxesPanelId;
  /** Width of the panel. */
  size: number;
  shouldShowHelpTip: boolean;
  onWorkspacesWidthChange: HandlerOf<number>;
  shouldHideAssignedConversations: boolean;
  hasLenses: boolean;
  hasTeamLenses: boolean;
}

/*
 * Style.
 */

const StyledEditSidebarHeader = styled.div`
  display: grid;
  grid-template-areas: 'title icon';
  grid-template-columns: min-content min-content;
  grid-gap: 2px;

  padding: 12px 0 17px 14px;
`;

const StyledEditSidebarText = styled.div`
  grid-area: title;
  min-width: 105px;

  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.veryLarge};

  color: ${(p) => p.theme.item};
`;

const StyledMoreDiv = styled.div`
  grid-area: icon;
  padding-top: 4px;
`;

const StyledInfoText = styled.div`
  padding: 3px 2px;
`;

/*
 * Component.
 */

export const WorkspacesEditor: FC<WorkspacesEditorProps> = (props) => {
  const {panelId} = props;
  const localize = useLocalize();
  const context = useWorkspacesEditor();
  const theme = useUserTheme();

  const renderEditSidebarHeader = () => (
    <>
      <StyledEditSidebarHeader>
        <StyledEditSidebarText>
          <Trans id="workspace-edit-sidebar">Edit sidebar</Trans>
        </StyledEditSidebarText>
        <StyledMoreDiv>
          <TooltipCoordinator
            render={() => (
              <StyledInfoText>
                <Trans id="workspaceseditor-info-tooltipbody">
                  Check to show. Uncheck to hide. If a checkbox is grayed out, the item can&apos;t be hidden.
                </Trans>
              </StyledInfoText>
            )}
            onShow={() =>
              trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_HOVER, {
                item: WorkspaceDisplayItemsEnum.INFO_TOOLTIP,
              })
            }
            iconColor={theme.icon}
            iconHoverColor={theme.iconHover}
            placement="right-start"
            maxWidth={240}
          />
        </StyledMoreDiv>
      </StyledEditSidebarHeader>
      {maybeRenderWorkspacesEditorAutoRecHelpTip()}
    </>
  );

  const maybeRenderWorkspacesEditorAutoRecHelpTip = () => {
    if (!props.shouldShowHelpTip) {
      return null;
    }
    return (
      <WorkspacesEditorAutoRecHelpTipContainer
        onToggleTag={context.onToggleTag}
        onToggleTeammate={context.onToggleTeammate}
        onToggleTeamCustomView={context.onToggleTeamCustomView}
        onToggleTeamInbox={context.onToggleTeamInbox}
        onToggleLens={context.onToggleLens}
        draftWorkspace={context.draftWorkspace}
        onWorkspacesWidthChange={props.onWorkspacesWidthChange}
        size={props.size}
      />
    );
  };

  const maybeRenderTeammatesWorkspaceEditor = () => {
    if (props.shouldHideAssignedConversations) {
      return null;
    }

    return (
      <TeammatesWorkspaceEditorContainer
        panelId={panelId}
        visualWorkspaceTeammates={context.draftWorkspace.teammates.items}
        onToggleTeammate={context.onToggleTeammate}
        collapsedItems={context.collapsedItems}
        onToggleExpandSection={context.onToggleExpandSection}
        onDropReorderTeammate={context.onDropReorderTeammate}
        onToggleDropdownTeammate={context.onToggleDropdownTeammate}
        teammateModelMap={context.teammateModelMap}
        isAlphabetized={context.draftWorkspace.isAlphabetized}
      />
    );
  };

  const maybeRenderLensesWorkspaceEditor = () => {
    if (!props.hasLenses) {
      return null;
    }

    return (
      <LensesWorkspaceEditorContainer
        panelId={panelId}
        visualWorkspaceLenses={context.draftWorkspace.lenses.items}
        collapsedItems={context.collapsedItems}
        onToggleExpandSection={context.onToggleExpandSection}
        isAlphabetized={context.draftWorkspace.isAlphabetized}
        onDropReorderLens={context.onDropReorderLens}
        onToggleLens={context.onToggleLens}
        onCreateLens={context.onCreateLens}
      />
    );
  };

  const renderTeamWorkspaces = () =>
    context.draftWorkspace.teams.map((teamData) => (
      <TeamWorkspaceEditorContainer
        key={teamData.teamId}
        panelId={panelId}
        onToggleTeamInbox={context.onToggleTeamInbox}
        onToggleDropdownTeamInbox={context.onToggleDropdownTeamInbox}
        onToggleTeamCustomView={context.onToggleTeamCustomView}
        teamData={teamData}
        collapsedItems={context.collapsedItems}
        onToggleExpandSection={context.onToggleExpandSection}
        isAlphabetized={context.draftWorkspace.isAlphabetized}
        teamInboxModelMap={context.teamInboxModelMap}
        teamCustomViewModelMap={context.teamCustomViewModelMap}
        onToggleDropdownTeamCustomView={context.onToggleDropdownTeamCustomView}
        onDropReorderTeamInbox={context.onDropReorderTeamInbox}
        onDropReorderTeamView={context.onDropReorderTeamView}
      />
    ));

  const renderWorkspaceEditor = () => (
    <nav
      aria-label={localize(
        defineMessage({
          id: 'workspaces-editor-region',
          message: 'Workspaces',
        }),
      )}
    >
      <TeammateWorkspaceEditorContainer
        panelId={panelId}
        visualWorkspaceRootFolders={context.draftWorkspace.rootFolders}
        visualWorkspaceNestedFolders={context.draftWorkspace.nestedFolders}
        onToggleRootFolder={context.onToggleRootFolder}
        collapsedItems={context.collapsedItems}
        visualWorkspaceTags={context.draftWorkspace.teammateWorkspaceTags}
        userNamespace={context.draftWorkspace.teammateNamespace}
        isAlphabetized={context.draftWorkspace.isAlphabetized}
        onToggleTag={context.onToggleTag}
        onToggleDropdownTag={context.onToggleDropdownTag}
        onToggleExpandSection={context.onToggleExpandSection}
        onToggleExpandTag={context.onToggleExpandTag}
        teamIds={context.draftWorkspace.teams.map((teamData) => teamData.teamId)}
        tagModelMap={context.tagModelMap}
        parentTagOverridesMap={context.parentTagOverridesMap}
        onDropNestCustomView={context.onDropNestTag}
        onDropReorderCustomView={context.onDropReorderTag}
        onTagCreated={context.onTagCreated}
        onDropReorderNestedFolder={context.onDropReorderNestedFolder}
      />
      {maybeRenderLensesWorkspaceEditor()}
      {renderTeamWorkspaces()}
      {maybeRenderTeammatesWorkspaceEditor()}
    </nav>
  );

  /*
   * Render.
   */

  // When the user navigates away, it's the equivalent of clicking 'cancel'.
  useEffect(() => {
    if (context.didBlockNavigation) {
      context.onRequestCancelChanges();
    }
  });

  return (
    <LeftPanel
      panelId={panelId}
      interactionId="workspaces-editor"
      bottomBar={<WorkspacesEditorFooter />}
      topBar={renderEditSidebarHeader()}
    >
      {renderWorkspaceEditor()}
    </LeftPanel>
  );
};
